import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { makerVariant } from '../variant-resolver.tsx';

gsap.registerPlugin(ScrollTrigger);

type Segment = { text: string; italic: boolean };

export const TextReveal = () => {
  // containerRef is both the GSAP pin target and the querySelectorAll root
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Variant-aware settings ──────────────────────────────────────────────────
  const textAlign = makerVariant('revealTextAlign', 'left');
  const showMaxWidth = makerVariant('revealShowMaxWidth', true) as any;
  const fontSize = makerVariant('revealFontSize', 'clamp(52px, 7vw, 92px)');
  const mobileFontSize = makerVariant('revealMobileFontSize', '40px');
  const mobilePaddingX = makerVariant('revealMobilePaddingX', '16px');
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth <= 767 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Text data ───────────────────────────────────────────────────────────────
  const para1Raw = makerVariant('revealPara1', [
    { text: 'AI agents now browse and buy for shoppers.', italic: false },
    { text: 'GreatStore', italic: true },
    { text: 'gets you ready for both.', italic: false },
    { text: 'Beautiful for', italic: false },
    { text: 'humans,', italic: true },
    { text: 'readable for', italic: false },
    { text: 'agents.', italic: true },
  ]);

  const para1: Segment[] = typeof para1Raw === 'string' ? JSON.parse(para1Raw) : para1Raw;

  // ── Render helpers ──────────────────────────────────────────────────────────
  const renderLine = (segments: Segment[], lineKey: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    segments.forEach((seg, sIdx) => {
      const words = seg.text.split(' ').filter(Boolean);
      words.forEach((word, wIdx) => {
        nodes.push(
          <span
            key={`${lineKey}-${sIdx}-${wIdx}`}
            className={`word-reveal ${seg.italic ? 'font-serif italic' : 'font-sans'}`}
            style={seg.italic ? { letterSpacing: '-0.02em' } : undefined}
          >
            {word}
          </span>
        );
        nodes.push(' ');
      });
    });
    return nodes;
  };

  const textStyle: React.CSSProperties = {
    fontSize: isMobile ? mobileFontSize : fontSize,
    lineHeight: 1.15,
    color: '#091717',
    margin: 0,
    textAlign: textAlign as any,
    letterSpacing: '-0.04em',
  };

  // ── GSAP animation ──────────────────────────────────────────────────────────
  useEffect(() => {
  const el = containerRef.current;
  if (!el) return;

  const ctx = (gsap as any).context(() => {
    const spans = el.querySelectorAll('.word-reveal');

    if (!spans.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(spans, { opacity: 1 });
      return;
    }

    gsap.set(spans, { opacity: 0 });

    gsap.to(spans, {
      opacity: 1,
      ease: 'none',
      stagger: 0.08,
      scrollTrigger: {
        trigger: el,
        start: 'top 80%',
        end: 'center 40%',
        scrub: 1,
        onLeave: (self: any) => { self.animation.progress(1); },
        onEnterBack: (self: any) => { self.animation.progress(1); },
      },
    });
  }, el);

  // 👇 Critical in production
  ScrollTrigger.refresh();

  return () => ctx.revert();
}, []);

  // ── Markup ──────────────────────────────────────────────────────────────────
  // No height: 300vh on the section — GSAP + pinSpacing adds the scroll room automatically.
  // No position: sticky on the inner div — GSAP's pin uses JS transforms instead.
  return (
    <section data-name="Text Reveal" data-nav-theme="light" style={{
      backgroundColor: '#fcfaf6',
      paddingTop: 'clamp(60px, 8vw, 120px)',
      paddingBottom: 'clamp(60px, 8vw, 120px)'
    }}>
      <div
        ref={containerRef}
        style={{
          maxWidth: '1360px',
          margin: '0 auto',
          paddingLeft: isMobile ? mobilePaddingX : '24px',
          paddingRight: isMobile ? mobilePaddingX : '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: makerVariant('revealJustifyContent', 'center'),
        }}
      >
        {/* Text block — max 1100px, centered inside 1360px constraint */}
        <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '1200px', marginRight: 'auto', textAlign: textAlign as any }}>

          <p style={textStyle}>
            {renderLine(para1, 'p1')}
          </p>

        </div>
      </div>
    </section>
  );
};
